/**
 * ChatInputStatusStrip Component
 *
 * A slim status strip that displays context information below the chat input (centered).
 * Shows: Repo (if known), Confidence %, Connection Status, and Execution Status
 *
 * This replaces the top bar (ContextHUD) by relocating status info to the
 * Soma Noesis chat input area where it's contextually relevant.
 *
 * IMPORTANT: Status Semantics (see MetaBuilder task "Fix Soma status semantics")
 * - Connection Status: Connected/Disconnected (backend reachability)
 * - Execution Status: Idle/Executing/Degraded (mission engine state)
 * - These are SEPARATE indicators and must NOT be collapsed into one
 * - "OFFLINE" must ONLY mean backend unreachable, NOT idle execution
 *
 * Design rules:
 * - Slim single-line strip
 * - Muted styling, no heavy background
 * - Must not look like a new header
 * - Responsive: wraps cleanly, no truncation
 * - If repo is unknown, omit repo chip instead of showing "unknown"
 *
 * See: MetaBuilder task "Remove top bar and relocate status into Soma chat input footer"
 * See: MetaBuilder task "Fix Soma status semantics (Connection vs Execution vs Confidence)"
 */

import React, { useState, useEffect, useCallback } from 'react';
import { studioFetch } from '../../studio/api';
import { useMetricsStore } from '../../services/MetricsPollerService';

interface StudioContext {
    repo: string | null;
    branch?: string | null;
    env: 'local' | 'staging' | 'production' | 'farm' | 'unknown';
    lane?: 'fast' | 'build' | 'unknown';
    missionId?: string;
    confidence: number | null;
    why: string;
    updatedAt: string;
    commitSha?: string | null;
    deployTimestamp?: string | null;
    execution?: {
        activeExecutors: number | null;
        queueDepth: number | null;
        lastMissionAt: string | null;
    };
}

interface ConnectionHealth {
    service: string;
    status: 'healthy' | 'degraded' | 'offline';
    lastCheckedAt: string;
    error?: string;
    detail?: Record<string, unknown>;
}

interface SplitHealthSignal {
    status: 'healthy' | 'degraded' | 'offline';
    issues: string[];
    optionalNotConfigured?: string[]; // List of optional services not configured
}

interface HealthSnapshot {
    services: ConnectionHealth[];
    overall: 'healthy' | 'degraded' | 'offline';
    // Split health signals for clearer display
    connectionHealth?: SplitHealthSignal;
    executionHealth?: SplitHealthSignal;
    timestamp: string;
}

const DEFAULT_CONTEXT: StudioContext = {
    repo: null,
    env: 'unknown',
    lane: 'unknown',
    confidence: null,
    why: 'Loading...',
    updatedAt: new Date().toISOString(),
};

const DEFAULT_HEALTH: HealthSnapshot = {
    services: [],
    overall: 'offline',
    timestamp: new Date().toISOString(),
};

const ChatInputStatusStrip: React.FC = () => {
    const [context, setContext] = useState<StudioContext>(DEFAULT_CONTEXT);
    const [health, setHealth] = useState<HealthSnapshot>(DEFAULT_HEALTH);
    const [showConfidenceTooltip, setShowConfidenceTooltip] = useState(false);
    const [showBackendTooltip, setShowBackendTooltip] = useState(false);
    const [showConnectionHealthTooltip, setShowConnectionHealthTooltip] = useState(false);
    const [showExecutionHealthTooltip, setShowExecutionHealthTooltip] = useState(false);
    // Track connection status separately from execution status
    const [isConnected, setIsConnected] = useState(false);

    // Subscribe to metrics store for rate limit indicator
    const { isRateLimited, rateLimitRetryAt } = useMetricsStore();

    // Dev-only: Get backend target from env vars for debugging
    const isDev = import.meta.env.DEV;
    const backendTarget =
        import.meta.env.VITE_WS_BASE || import.meta.env.VITE_API_BASE || 'unknown';

    const fetchContext = useCallback(async () => {
        try {
            const response = await studioFetch('/api/studio/context');
            if (response.ok) {
                const data = await response.json();
                setContext(data.context || DEFAULT_CONTEXT);
                // Successfully fetched = connected
                setIsConnected(true);
            } else {
                setIsConnected(false);
            }
        } catch (err) {
            console.warn('[ChatInputStatusStrip] Failed to fetch context:', err);
            setIsConnected(false);
        }
    }, []);

    const fetchHealth = useCallback(async () => {
        try {
            const response = await studioFetch('/api/studio/health');
            if (response.ok) {
                const data = await response.json();
                setHealth(data.health || DEFAULT_HEALTH);
            }
        } catch (err) {
            console.warn('[ChatInputStatusStrip] Failed to fetch health:', err);
        }
    }, []);

    useEffect(() => {
        fetchContext();
        fetchHealth();

        // Poll context every 10s
        const contextInterval = setInterval(fetchContext, 10000);
        // Poll health every 30s
        const healthInterval = setInterval(fetchHealth, 30000);

        return () => {
            clearInterval(contextInterval);
            clearInterval(healthInterval);
        };
    }, [fetchContext, fetchHealth]);

    const getConfidenceColor = (confidence: number | null) => {
        if (confidence === null) return 'text-slate-400';
        // Confidence is on 0-100 scale from SystemStatusService
        if (confidence >= 80) return 'text-emerald-400';
        if (confidence >= 50) return 'text-amber-400';
        return 'text-red-400';
    };

    /**
     * Derive Execution Status from health.overall
     * IMPORTANT: "offline" in health.overall does NOT mean disconnected
     * It means "no execution signal detected" which should display as "Execution Idle"
     *
     * Mapping:
     * - healthy -> Executing (active execution)
     * - degraded -> Degraded (execution blocked/unhealthy)
     * - offline -> Execution Idle (no active missions, NOT disconnected)
     */
    const getExecutionStatus = (
        healthOverall: string
    ): 'Executing' | 'Execution Idle' | 'Degraded' => {
        switch (healthOverall) {
            case 'healthy':
                return 'Executing';
            case 'degraded':
                return 'Degraded';
            case 'offline':
            default:
                return 'Execution Idle';
        }
    };

    const getExecutionStatusColor = (status: string) => {
        switch (status) {
            case 'Executing':
                return 'text-emerald-400';
            case 'Degraded':
                return 'text-amber-400';
            case 'Execution Idle':
            default:
                return 'text-slate-400';
        }
    };

    const getExecutionStatusDotColor = (status: string) => {
        switch (status) {
            case 'Executing':
                return 'bg-emerald-400';
            case 'Degraded':
                return 'bg-amber-400';
            case 'Execution Idle':
            default:
                return 'bg-slate-500';
        }
    };

    // Helper for split health signal colors
    const getHealthSignalColor = (status: 'healthy' | 'degraded' | 'offline') => {
        switch (status) {
            case 'healthy':
                return 'text-emerald-400';
            case 'degraded':
                return 'text-amber-400';
            case 'offline':
                return 'text-red-400';
        }
    };

    const getHealthSignalDotColor = (status: 'healthy' | 'degraded' | 'offline') => {
        switch (status) {
            case 'healthy':
                return 'bg-emerald-400';
            case 'degraded':
                return 'bg-amber-400';
            case 'offline':
                return 'bg-red-400';
        }
    };

    const getHealthSignalLabel = (status: 'healthy' | 'degraded' | 'offline') => {
        switch (status) {
            case 'healthy':
                return 'Healthy';
            case 'degraded':
                return 'Degraded';
            case 'offline':
                return 'Offline';
        }
    };

    // Don't show repo chip if unknown
    const showRepo = context.repo && context.repo !== 'unknown';

    // Derive execution status from health (NOT connection status)
    const executionStatus = getExecutionStatus(health.overall);

    // Check if confidence is unavailable (null means no real data yet)
    const isConfidenceUnavailable = context.confidence === null;
    // Check if confidence is 0 due to no recent executions
    const isConfidenceZeroDueToNoRuns =
        context.confidence === 0 && executionStatus === 'Execution Idle';

    return (
        <div
            className="flex flex-col items-center gap-1 px-6 py-1.5 text-xs text-slate-400"
            data-testid="chat-input-status-strip"
        >
            {/* Row 1: Repo, Confidence, Connection Status */}
            <div className="flex items-center justify-center gap-3 flex-wrap">
                {/* Repo badge - only shown if known */}
                {showRepo && (
                    <div className="flex items-center gap-1.5">
                        <span className="text-slate-500">Repo:</span>
                        <span className="font-mono text-slate-300 bg-slate-800/50 px-1.5 py-0.5 rounded">
                            {context.repo}
                            {context.branch && (
                                <span className="text-slate-500 ml-1">@{context.branch}</span>
                            )}
                        </span>
                    </div>
                )}

                {/* Confidence badge with tooltip - shows "N/A" when null, "No recent runs" context when 0% */}
                <div
                    className="relative flex items-center gap-1.5"
                    onMouseEnter={() => setShowConfidenceTooltip(true)}
                    onMouseLeave={() => setShowConfidenceTooltip(false)}
                >
                    <span className="text-slate-500">Confidence:</span>
                    <span
                        className={`font-medium ${getConfidenceColor(context.confidence)} cursor-help`}
                    >
                        {isConfidenceUnavailable ? (
                            'N/A'
                        ) : (
                            <>
                                {Math.round(context.confidence!)}%
                                {isConfidenceZeroDueToNoRuns && (
                                    <span className="text-slate-500 ml-1">• No recent runs</span>
                                )}
                            </>
                        )}
                    </span>
                    {showConfidenceTooltip && (
                        <div className="absolute bottom-full left-0 mb-1 p-2 bg-slate-800 border border-slate-700 rounded shadow-lg z-50 min-w-[180px] max-w-[280px]">
                            <p className="text-xs text-slate-300">{context.why}</p>
                        </div>
                    )}
                </div>

                {/* Connection Status - backend reachability (Connected/Disconnected)
                    Dev-only: Shows backend target in tooltip to prevent staging/production confusion */}
                <div
                    className="relative flex items-center gap-1.5"
                    data-testid="connection-status"
                    onMouseEnter={() => isDev && setShowBackendTooltip(true)}
                    onMouseLeave={() => setShowBackendTooltip(false)}
                >
                    <div
                        className={`w-1.5 h-1.5 rounded-full ${isConnected ? 'bg-emerald-400' : 'bg-red-400'}`}
                    />
                    <span
                        className={`font-medium ${isConnected ? 'text-emerald-400' : 'text-red-400'} ${isDev ? 'cursor-help' : ''}`}
                    >
                        {isConnected ? 'Connected' : 'Disconnected'}
                    </span>
                    {/* Dev-only tooltip showing backend target */}
                    {isDev && showBackendTooltip && (
                        <div className="absolute bottom-full left-0 mb-1 p-2 bg-slate-800 border border-slate-700 rounded shadow-lg z-50 min-w-[200px] max-w-[320px]">
                            <p className="text-xs text-slate-400 mb-1">Backend Target:</p>
                            <p className="text-xs text-slate-300 font-mono break-all">
                                {backendTarget}
                            </p>
                        </div>
                    )}
                </div>

                {/* Rate Limit Indicator - shows when metrics polling is rate limited (429)
                    Calm, quiet indicator - no spam, just truth
                    See: MetaBuilder task "Metrics Polling Consolidation and Throttle" */}
                {isRateLimited && (
                    <div
                        className="flex items-center gap-1.5 text-amber-400/70"
                        data-testid="rate-limit-indicator"
                    >
                        <span className="text-slate-500">•</span>
                        <span className="text-xs">
                            Metrics rate limited
                            {rateLimitRetryAt && (
                                <span className="text-slate-500 ml-1">
                                    • retry in {Math.ceil((rateLimitRetryAt - Date.now()) / 1000)}s
                                </span>
                            )}
                        </span>
                    </div>
                )}
            </div>

            {/* Row 2: Split Health Signals - Connection Health vs Execution Health */}
            <div className="flex items-center justify-center gap-4 flex-wrap">
                {/* Connection Health - external service connectivity */}
                <div
                    className="relative flex items-center gap-1.5 cursor-help"
                    data-testid="connection-health"
                    onMouseEnter={() => setShowConnectionHealthTooltip(true)}
                    onMouseLeave={() => setShowConnectionHealthTooltip(false)}
                >
                    <div
                        className={`w-1.5 h-1.5 rounded-full ${getHealthSignalDotColor(health.connectionHealth?.status || 'offline')}`}
                    />
                    <span className="text-slate-500">Services:</span>
                    <span
                        className={`font-medium ${getHealthSignalColor(health.connectionHealth?.status || 'offline')}`}
                    >
                        {getHealthSignalLabel(health.connectionHealth?.status || 'offline')}
                    </span>
                    {/* Tooltip showing connection health issues and optional services */}
                    {showConnectionHealthTooltip && (
                        <div className="absolute bottom-full left-0 mb-1 p-2 bg-slate-800 border border-slate-700 rounded shadow-lg z-50 min-w-[200px] max-w-[320px]">
                            <p className="text-xs text-slate-400 mb-1">Connection Health:</p>
                            {health.connectionHealth?.issues &&
                            health.connectionHealth.issues.length > 0 ? (
                                <ul className="text-xs text-slate-300 list-disc list-inside">
                                    {health.connectionHealth.issues.map((issue, i) => (
                                        <li key={i}>{issue}</li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="text-xs text-emerald-400">All services healthy</p>
                            )}
                            {/* Show optional services that are not configured */}
                            {health.connectionHealth?.optionalNotConfigured &&
                                health.connectionHealth.optionalNotConfigured.length > 0 && (
                                    <div className="mt-2 pt-2 border-t border-slate-700">
                                        <p className="text-xs text-slate-500 mb-1">
                                            Optional (not configured):
                                        </p>
                                        <p className="text-xs text-slate-400">
                                            {health.connectionHealth.optionalNotConfigured.join(
                                                ', '
                                            )}
                                        </p>
                                    </div>
                                )}
                        </div>
                    )}
                </div>

                {/* Execution Health - V2 runtime and bee readiness */}
                <div
                    className="relative flex items-center gap-1.5 cursor-help"
                    data-testid="execution-health"
                    onMouseEnter={() => setShowExecutionHealthTooltip(true)}
                    onMouseLeave={() => setShowExecutionHealthTooltip(false)}
                >
                    <div
                        className={`w-1.5 h-1.5 rounded-full ${getHealthSignalDotColor(health.executionHealth?.status || 'offline')}`}
                    />
                    <span className="text-slate-500">Execution:</span>
                    <span
                        className={`font-medium ${getHealthSignalColor(health.executionHealth?.status || 'offline')}`}
                    >
                        {getHealthSignalLabel(health.executionHealth?.status || 'offline')}
                    </span>
                    {/* Tooltip showing execution health issues */}
                    {showExecutionHealthTooltip && (
                        <div className="absolute bottom-full left-0 mb-1 p-2 bg-slate-800 border border-slate-700 rounded shadow-lg z-50 min-w-[200px] max-w-[320px]">
                            <p className="text-xs text-slate-400 mb-1">Execution Health:</p>
                            {health.executionHealth?.issues &&
                            health.executionHealth.issues.length > 0 ? (
                                <ul className="text-xs text-slate-300 list-disc list-inside">
                                    {health.executionHealth.issues.map((issue, i) => (
                                        <li key={i}>{issue}</li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="text-xs text-emerald-400">Execution engine ready</p>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ChatInputStatusStrip;
